import { useState, useContext, type ChangeEvent } from "react";
import { useDisclosure } from "hooks/use-disclosure";

import { ConfirmationDialog } from "ui/confirmation-dialog";
import { LoadingButton } from "ui/button";
import { TextArea } from "ui/text-area";

import CommentContext from "contexts/comment-context";

import { TouchpointStatuses } from "types";

export interface TouchpointSendFeedbackButtonProps {
  onSendFeedback: (status: string) => Promise<void>;
  isLoading: boolean;
}

export const TouchpointSendFeedbackButton = ({
  onSendFeedback,
  isLoading,
}: TouchpointSendFeedbackButtonProps) => {
  const confirmModal = useDisclosure();
  const [comment, setComment] = useState("");
  const { submitNewComment } = useContext(CommentContext);

  const handleSendFeedback = async () => {
    try {
      if (comment) {
        const payload = {
          actionItem: true,
          text: comment,
        };

        await submitNewComment(payload);
      }

      await onSendFeedback(TouchpointStatuses.REJECTED);

      confirmModal.onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  return (
    <>
      <LoadingButton
        state={isLoading ? "loading" : "default"}
        loadingMessage="Sending feedback..."
        successMessage="Feedback sent"
        errorMessage="Error. Try again."
        variant="text"
        size="large"
        onClick={() => {
          confirmModal.onOpen();
        }}>
        Send feedback
      </LoadingButton>
      <ConfirmationDialog
        open={confirmModal.isOpen}
        headline="Ready to send feedback?"
        message="If you have any additional comments, please add it here. Once our team reviews your feedback, we'll send a revised version back for your team's approval."
        cancelButtonText="Cancel"
        confirmButtonText="Send feedback"
        confirmLoadingMessage="Sending feedback..."
        onConfirm={handleSendFeedback}
        onClose={() => {
          confirmModal.onClose();
        }}
        intent="warning">
        <TextArea
          readOnly={false}
          label=""
          helperText=""
          value={comment}
          placeholder="Type..."
          onChange={(e) => {
            handleChange(e);
          }}
          data-testid="send-feedback-teaxtarea"
          resize="vertical"
        />
      </ConfirmationDialog>
    </>
  );
};
